import {
  Box,
  Container,
  Grid,
  LinearProgress,
  SxProps,
  Theme,
  Typography,
  type BoxProps,
  type TypographyProps,
} from '@mui/material';

import React, { PropsWithChildren, memo } from 'react';
import { Breadcrumb, Breadcrumbs } from './Breadcrumbs';

interface PageTemplateProps {
  breadcrumbs: Breadcrumb[];
  right?: React.ReactNode;
  cta?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  description?: React.ReactNode;
  loading?: boolean;
  fluid?: boolean;
  secondaryRow?: React.ReactNode;
  centered?: boolean;
  livechat?: boolean;
  sx?: SxProps<Theme>;
}

interface TitleComponentProps extends BoxProps {
  children?: React.ReactNode;
  centered?: boolean;
  cta?: React.ReactNode;
  typographyProps?: TypographyProps;
}
export const TitleComponent = ({
  children,
  centered,
  cta,
  typographyProps,
  ...boxProps
}: TitleComponentProps) => {
  const { sx, ...rest } = boxProps;
  const { sx: typographySx, ...typographyRest } = typographyProps || {};
  return (
    <Box
      className="page-template-title-component"
      sx={{
        display: 'flex',
        justifyContent: centered ? 'center' : 'space-between',
        alignItems: 'center',
        alignContent: 'center',
        justifyItems: 'center',
        ...sx,
      }}
      {...rest}>
      <Typography
        variant="h4"
        // eslint-disable-next-line react/no-children-prop
        children={children}
        component="h1"
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: centered ? 'center' : 'space-between',
          alignItems: 'center',
          alignContent: 'center',
          justifyItems: 'center',
          textAlign: centered ? 'center' : undefined,
          pt: centered ? 4 : undefined,
          ...typographySx,
        }}
        {...typographyRest}></Typography>
      {cta}
    </Box>
  );
};

const TitleContent = memo(
  ({
    title,
    subtitle,
    cta,
    centered,
    description,
  }: Pick<
    PageTemplateProps,
    'title' | 'subtitle' | 'description' | 'cta' | 'centered'
  >) => (
    <>
      {typeof title === 'string' && (
        <TitleComponent centered={centered} cta={cta}>
          {title}
        </TitleComponent>
      )}
      {typeof title === 'object' && title}
      {subtitle && (
        <Typography variant="body2" component="h6" sx={{ mt: 2 }}>
          {subtitle}
        </Typography>
      )}
      {description && (
        <Typography variant="subtitle1" component="h2" sx={{ mt: 2 }}>
          {description}
        </Typography>
      )}
    </>
  )
);

const LoadingIndicator = memo(({ loading }: { loading?: boolean }) => {
  if (!loading) {
    return null;
  }
  return (
    <Box sx={{ width: '100%' }}>
      <LinearProgress variant="query" />
    </Box>
  );
});

export const PageTemplate = memo(
  ({
    breadcrumbs,
    children,
    title,
    subtitle,
    description,
    loading,
    fluid,
    cta,
    right,
    secondaryRow,
    centered,
    livechat,
    ...boxProps
  }: PropsWithChildren<PageTemplateProps>) => {
    return (
      <Box
        className="page-template"
        sx={{ mt: '-3px', pt: fluid ? 0 : undefined, pb: 10 }}
        {...boxProps}>
        {breadcrumbs?.length > 0 && (
          <Breadcrumbs breadcrumbs={breadcrumbs} livechat={livechat} />
        )}
        {secondaryRow ? secondaryRow : null}
        <LoadingIndicator loading={loading} />
        <Container
          className="page-template-content"
          disableGutters={fluid}
          maxWidth={fluid ? false : undefined}
          sx={{ paddingTop: fluid ? undefined : 4 }}>
          {(title || subtitle || description || right) && (
            <Grid container spacing={fluid ? 0 : 4}>
              <Grid
                item
                xs={12}
                lg={right ? 7 : 12}
                alignItems="center"
                sx={centered ? { textAlign: 'center' } : undefined}>
                <TitleContent
                  title={title}
                  subtitle={subtitle}
                  description={description}
                  cta={cta}
                  centered={centered}
                />
              </Grid>
              {right && (
                <Grid
                  item
                  xs={12}
                  lg={5}
                  display="flex"
                  justifyContent="end"
                  alignItems="center">
                  {right}
                </Grid>
              )}
              <Box sx={{ marginBottom: 6 }} />
            </Grid>
          )}
          <Grid container spacing={fluid ? 0 : 4}>
            <Grid
              item
              xs={12}
              className="page-template-children"
              sx={{ mt: fluid ? undefined : 4 }}>
              {children}
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }
);
